import {
  useLazyGetPaginatedPostsByAdminQuery,
  usePutPaginatedPostsByAdminMutation,
} from 'app/services/post.service'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import KGrid from 'components/kendo/grid/KGrid'
import styled, { css } from 'styled-components'
import { GridColumn } from '@progress/kendo-react-grid'
import { BUTTONS, ERRORS, FORM, MODALS, POST, TABLE } from 'locales/locales'
import { FixMeLater } from 'types'
import CustomIcon from 'components/icon/CustomIcon'
import { useAppSelector } from 'app/hooks'
import { IRolePermissions, NewsFeedPermissions } from 'types/role.types'
import { isFlagSetIn } from 'utils/helpers/isSetInFlags'
import { icons } from 'utils/icons'
import useInfoModal from 'utils/hooks/useInfoModal'
import DropdownAlert from 'components/dropdown-alert/DropdownAlert'

interface DataStateFilter {
  field: string
  operator: string
  value: boolean | string
}
interface DataState {
  take: number
  skip: number
  filter: { logic: string; filters: DataStateFilter[] }
  sort?: Array<{ field: string; dir: string }>
}

const initialDataState: DataState = {
  take: 10,
  skip: 0,
  filter: { logic: 'and', filters: [{ field: 'isDeleted', operator: 'eq', value: false }] },
  sort: [],
}

const NewsfeedManagement = () => {
  const { t } = useTranslation()
  const [dataState, setDataState] = useState(initialDataState)
  const [getPaginatedPostsByAdmin] = useLazyGetPaginatedPostsByAdminQuery()
  const [putPaginatedPostsByAdmin] = usePutPaginatedPostsByAdminMutation()
  const [isLoading, setIsLoading] = useState(false)
  const [newsfeeds, setNewsfeeds] = useState({ data: [] as FixMeLater[], totalCount: 1 })

  const permissions = useAppSelector<IRolePermissions>((state) => state.auth.permissions)
  const hasArchiveNewsfeedPermission = isFlagSetIn(
    [NewsFeedPermissions.EDIT_DELETE_NEWSFEED],
    permissions.newsFeed,
  )

  const { openModal, closeModal, Modal } = useInfoModal()

  useEffect(() => {
    setIsLoading(true);
    fetchNewsfeeds();
  }, [dataState])


  const fetchNewsfeeds = useCallback(async () => {
    const data = await getPaginatedPostsByAdmin({ ...dataState, count: true })
    if (data.data) setNewsfeeds(data.data)
    setIsLoading(false)
  }, [dataState])


  const handleFilterChange = useCallback((event: FixMeLater) => {
    setDataState((s) => ({ ...s, filter: event.filter }))
  }, [])

  const onDataStateChange = useCallback((event: FixMeLater) => {
    setDataState((s) => ({
      ...s,
      skip: event.dataState.skip,
      take: event.dataState.take,
      sort: event.dataState.sort || [],
    }))
  }, [])

  const onConfirmArchiveNewsfeed = async (dataItem: FixMeLater, isDeleted: boolean) => {
    const response: FixMeLater = await putPaginatedPostsByAdmin({
      filter: { _id: dataItem._id },
      entity: { $set: { isDeleted: isDeleted } },
    })



    if (response?.data?.success) {

      DropdownAlert.ref.show({
        isError: false,
        message: t("SUCCESS.NEWSFEED_ARCHIVED_SUCCESSFULLY"),
      })
      closeModal()
      fetchNewsfeeds();
    } else {
      DropdownAlert.ref.show({
        isError: true,
        message: t("ERRORS.FAILED_TO_FORWARD_NEWSFEED"),
      })
      closeModal()
    }
  
  }

  const onToggleArchive = (newsfeed: FixMeLater, index: number) => {
    if (newsfeeds?.data?.[index]) {
      const { isDeleted } = newsfeeds.data[index]
      openModal({
        title: isDeleted ? t(MODALS.ACTIVE_NEWSFEED_TITLE) : t(MODALS.INACTIVE_NEWSFEED_TITLE),
        description: isDeleted
          ? t(MODALS.ACTIVE_NEWSFEED_DESCRIPTION)
          : t(MODALS.INACTIVE_NEWSFEED_DESCRIPTION),
        cancelTitle: t(BUTTONS.CANCEL) as string,
        okTitle: t(BUTTONS.YES) as string,
        onCancel: closeModal,
        onOk: () => onConfirmArchiveNewsfeed(newsfeed, !isDeleted),
      })
    }
  }

  const ActionCell = useCallback(
    ({ dataItem, dataIndex }: FixMeLater) => {
      return (
        <td>
          <ButtonContainer role="group" aria-label="actions">
            {hasArchiveNewsfeedPermission && (
              <IconButton
                aria-label="archive"
                onClick={() => onToggleArchive(dataItem, dataIndex)}
                title={t(TABLE.CHANNEL_ACTIONS) as string}
              >
                <CustomIcon iconPreset="grande" source={icons.archiveIcon} alt="archive" />
              </IconButton>
            )}
          </ButtonContainer>
        </td>
      )
    },
    [hasArchiveNewsfeedPermission, newsfeeds?.data],
  )

  const totalText = useMemo(() => `${newsfeeds.totalCount ?? 0}`, [newsfeeds.totalCount])

  return (
    <Page>
      <ContentContainer>
        <HeaderBar>
          <TitleRow>
            <H1>Newsfeed Management</H1>
            <CountPill>{totalText}</CountPill>
          </TitleRow>
          <SubtleHint>
            {t('Arama / sıralama için sütun başlıklarını kullanabilirsiniz.')}
          </SubtleHint>
        </HeaderBar>

        <TableCard>
          <KGrid
            loading={isLoading}
            datasource={newsfeeds.data}
            dataState={dataState}
            pageable
            total={newsfeeds.totalCount | 0}
            filterable={true}
            editMode={false}
            sortable
            onFilterChange={handleFilterChange}
            onDataStateChange={onDataStateChange}
            style={{ height: '100%' }}
          >
            <GridColumn field="users.fullname" filterable={false} title={t(POST.CREATOR)} />
            <GridColumn field="content.header" title={t(POST.HEADER)} />
            <GridColumn field="content.value" title={t(POST.DESCRIPTION)} />
            <GridColumn field="groups.name" filterable={false} title={t(FORM.TEAM)} />
            <GridColumn
              field="postId"
              title={t(POST.CONTENT_TYPE)}
              sortable={false}
              filterable={false}
              cells={{
                data: (props: any) =>
                  props.dataItem.postId ? (
                    <TypePill key={props.dataItem.postId}>{t(POST.COMMENT)}</TypePill>
                  ) : (
                    <TypePill $alt>{t(POST.POST)}</TypePill>
                  ),
              }}
            />
            <GridColumn
              field="actions"
              sortable={false}
              filterable={false}
              title={t(TABLE.CHANNEL_ACTIONS)}
              cells={{ data: (props: any) => <ActionCell {...props} /> }}
            />
          </KGrid>

          {!isLoading && (!newsfeeds?.data || newsfeeds.data.length === 0) && (
            <EmptyState>
              <EmptyTitle>Hiç içerik yok</EmptyTitle>
              <EmptyText>Yeni bir gönderi eklediğinizde burada listelenecek.</EmptyText>
            </EmptyState>
          )}
        </TableCard>

        {Modal}
      </ContentContainer>
    </Page>
  )
}

/* -------------------- THEME HELPERS -------------------- */
const tc = (p: any, path: string, fb: string) => {
  const parts = path.split('.')
  let v: any = p?.theme
  for (const k of parts) v = v?.[k]
  return v ?? fb
}

const softCard = css`
  background: ${({ theme }) => tc({ theme } as any, 'colors.bgElevated', 'rgba(255,255,255,0.7)')};
  border: 1px solid ${({ theme }) => tc({ theme } as any, 'colors.border', 'rgba(0,0,0,0.08)')};
  backdrop-filter: blur(6px);
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
`

/* -------------------- LAYOUT -------------------- */
const Page = styled.div`
  display: grid;
  gap: 16px;
`

const HeaderBar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e6e6;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const H1 = styled.h1`
  margin: 0;
  font-size: clamp(18px, 1.8vw, 24px);
  font-weight: 700;
  color: ${({ theme }) => tc({ theme } as any, 'colors.textPrimary', '#111')};
`

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ theme }) => tc({ theme } as any, 'colors.accentSoft', 'rgba(99,102,241,0.12)')};
  color: ${({ theme }) => tc({ theme } as any, 'colors.accent', '#6366f1')};
  border: 1px solid
    ${({ theme }) => tc({ theme } as any, 'colors.accentBorder', 'rgba(99,102,241,0.35)')};
`

const SubtleHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => tc({ theme } as any, 'colors.textMuted', '#6b7280')};
`

/* -------------------- CARD & GRID -------------------- */
const TableCard = styled.div`
  ${softCard};
  padding: 12px;

  /* Kendo Grid fine-tuning */
  .k-grid,
  .k-table {
    background: transparent;
    color: ${({ theme }) => tc({ theme } as any, 'colors.textPrimary', '#111')};
  }

  /* Sticky header */
  .k-table-thead,
  .k-grid-header {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${({ theme }) => tc({ theme } as any, 'colors.bgElevated', '#fff')};
    backdrop-filter: blur(6px);
  }

  /* Head cells */
  .k-table-th,
  .k-grid-header .k-header {
    font-weight: 700;
    font-size: 12px;
    color: ${({ theme }) => tc({ theme } as any, 'colors.textMuted', '#6b7280')};
    border-bottom: 1px solid
      ${({ theme }) => tc({ theme } as any, 'colors.border', 'rgba(0,0,0,0.08)')};
  }

  /* Rows: zebra + hover */
  .k-table-row:nth-child(odd) .k-table-td {
    background: ${({ theme }) => tc({ theme } as any, 'colors.rowAlt', 'rgba(0,0,0,0.015)')};
  }
  .k-table-row:hover .k-table-td {
    background: ${({ theme }) => tc({ theme } as any, 'colors.rowHover', 'rgba(99,102,241,0.06)')};
  }

  /* Cells */
  .k-table-td {
    border-bottom: 1px dashed
      ${({ theme }) => tc({ theme } as any, 'colors.border', 'rgba(0,0,0,0.06)')};
    vertical-align: middle;
  }

  /* Pager */
  .k-pager {
    border-top: 1px solid
      ${({ theme }) => tc({ theme } as any, 'colors.border', 'rgba(0,0,0,0.08)')};
    padding-top: 8px;
  }
`

/* -------------------- ACTIONS -------------------- */
const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  flex-wrap: wrap;
`

const IconButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.12s ease, background-color 0.15s ease, box-shadow 0.15s ease;
  background: ${({ theme }) => tc({ theme } as any, 'colors.bgSoft', 'transparent')};

  &:hover {
    background: ${({ theme }) => tc({ theme } as any, 'colors.hover', 'rgba(0,0,0,0.06)')};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => tc({ theme } as any, 'colors.accent', '#6366f1')};
    outline-offset: 2px;
  }

  /* Icon içinde svg varsa büyüklük dengesi */
  svg,
  img {
    width: 18px;
    height: 18px;
  }
`

/* -------------------- PILLS -------------------- */
const TypePill = styled.span<{ $alt?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  margin-top: 18px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  border-radius: 999px;
  border: 1px solid;
  ${({ $alt, theme }) =>
    $alt
      ? css`
          background: ${tc({ theme } as any, 'colors.successSoft', 'rgba(16,185,129,0.12)')};
          color: ${tc({ theme } as any, 'colors.success', '#10b981')};
          border-color: ${tc({ theme } as any, 'colors.successBorder', 'rgba(16,185,129,0.35)')};
        `
      : css`
          background: ${tc({ theme } as any, 'colors.infoSoft', 'rgba(59,130,246,0.12)')};
          color: ${tc({ theme } as any, 'colors.info', '#3b82f6')};
          border-color: ${tc({ theme } as any, 'colors.infoBorder', 'rgba(59,130,246,0.35)')};
        `}
`

/* -------------------- EMPTY STATE -------------------- */
const EmptyState = styled.div`
  display: grid;
  justify-items: center;
  gap: 6px;
  padding: 36px 12px;
  color: ${({ theme }) => tc({ theme } as any, 'colors.textMuted', '#6b7280')};
`

const EmptyTitle = styled.div`
  font-weight: 700;
`

const EmptyText = styled.div`
  font-size: 12px;
`

const ContentContainer = styled.div`
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`

const HeaderBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #e6e6e6;
`

// Styled Components

const PageTitle = styled.h1`
  color: #2a5885;
  font-size: 1.75rem;
  margin: 0;
  font-weight: 600;
`

export default NewsfeedManagement
